// SiteMode context — exposes the install-wide product mode chosen at
// /setup so leaf routes can branch UX without prop-drilling. The value
// is sourced from `/api/v1/setup/status` at the top of App.tsx and
// piped through this provider.
//
// Why a context rather than re-fetching: setup status is already
// fetched once on app boot, and every leaf route shares the same
// value for the lifetime of the session. Re-fetching per route would
// hit the API repeatedly and risk flapping during the brief window
// while the request is in flight.

import { createContext, useContext, type ReactNode } from 'react';
import type { SiteMode } from '../lib/setupStatus.js';

const SiteModeContext = createContext<SiteMode | null>(null);

export interface SiteModeProviderProps {
  mode: SiteMode;
  children: ReactNode;
}

export function SiteModeProvider({ mode, children }: SiteModeProviderProps) {
  return (
    <SiteModeContext.Provider value={mode}>{children}</SiteModeContext.Provider>
  );
}

/**
 * Returns the active install's site mode. Throws if the calling
 * component is rendered outside a SiteModeProvider — every authed
 * route is wrapped in App.tsx so this should never fire in
 * production; it's strictly a guardrail for tests that forget to
 * wrap the page.
 */
export function useSiteMode(): SiteMode {
  const value = useContext(SiteModeContext);
  if (value === null) {
    throw new Error(
      'useSiteMode must be called inside a <SiteModeProvider>. ' +
        'Wrap your test render with <SiteModeProvider mode="...">.',
    );
  }
  return value;
}
